import { useLocation, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Section, Container } from '@/components/shared/Section';
import { GlassPanel } from '@/components/shared/GlassPanel';
import { ProductFrame } from '@/components/shared/ProductFrame';
import { SignalChip } from '@/components/shared/SignalChip';
import { AnimatedReveal } from '@/components/shared/AnimatedReveal';
import { SEO, breadcrumbSchema } from '@/components/shared/SEO';
import { fadeInUp, staggerContainer, viewportOnce } from '@/lib/motion';
import { Check, ArrowRight, ChevronRight } from 'lucide-react';

const seoPages = {
  '/meta-ads-spy-tool': {
    title: 'Meta Ads Spy Tool',
    h1: 'The most powerful Meta Ads spy tool for Shopify merchants',
    subtitle: 'See exactly what ads your competitors are running on Facebook and Instagram. Analyze creatives, copy, and targeting — all in real time.',
    sections: [
      { heading: 'Why spy on Meta ads?', body: 'Meta remains the largest advertising platform for e-commerce. Your competitors are spending thousands every day on Facebook and Instagram ads. Without visibility into their strategies, you are flying blind. RADAR gives you complete transparency into competitor Meta advertising.' },
      { heading: 'What you can track', body: 'RADAR monitors all Meta ad formats including video, image, carousel, and collection ads. You can see competitor ad copy, headlines, calls to action, landing pages, and estimated campaign duration.' },
      { heading: 'How RADAR compares', body: 'Unlike basic ad library tools, RADAR provides real-time monitoring, automated alerts, and deep analytics. We do not just show you ads — we help you understand competitor strategy and act on it.' },
    ],
    features: ['Real-time ad detection across Facebook and Instagram', 'Creative archive with search and filtering', 'Ad copy and CTA analysis', 'Landing page monitoring and capture', 'Campaign timeline and duration tracking', 'Automated alerts for new competitor campaigns'],
    cta: 'Try RADAR free for 7 days',
    relatedLinks: [{ label: 'Meta Ads Library Alternative', href: '/meta-ads-library-alternative' }, { label: 'Free Meta Ads Search', href: '/tools/meta-ads-search' }],
  },
  '/tiktok-ads-spy-tool': {
    title: 'TikTok Ads Spy Tool',
    h1: 'Track every TikTok ad your competitors create',
    subtitle: 'Monitor TikTok ad creatives, trends, and strategies. Stay ahead of the fastest-growing advertising platform.',
    sections: [
      { heading: 'Why TikTok ad intelligence matters', body: 'TikTok is where the next generation of e-commerce buyers discover products. Competitors who crack TikTok advertising gain a massive advantage. RADAR helps you see what works — before you spend a dollar.' },
      { heading: 'What RADAR tracks on TikTok', body: 'We monitor all TikTok ad formats including In-Feed, TopView, Spark Ads, and branded effects. Track competitor video creatives, ad copy, hashtags, sounds, and campaign duration.' },
      { heading: 'Built for e-commerce teams', body: 'RADAR is specifically designed for Shopify merchants and e-commerce brands. Every feature is built to help you compete more effectively on TikTok.' },
    ],
    features: ['Full TikTok Ad Library monitoring', 'Video creative archiving and download', 'Hashtag and sound tracking', 'Campaign duration analysis', 'Trend detection algorithms', 'Automated alerts for new campaigns'],
    cta: 'Try RADAR free for 7 days',
    relatedLinks: [{ label: 'Free TikTok Ads Search', href: '/tools/tiktok-ads-search' }, { label: 'TikTok Ads Solution', href: '/solutions/tiktok-ads' }],
  },
  '/meta-ads-library-alternative': {
    title: 'Meta Ads Library Alternative',
    h1: 'A better alternative to Meta Ads Library',
    subtitle: 'RADAR goes beyond what Meta Ads Library offers — with real-time monitoring, automated alerts, competitive analytics, and actionable insights.',
    sections: [
      { heading: 'Limitations of Meta Ads Library', body: 'The Meta Ads Library is a free resource, but it has significant limitations. No alerts, no competitive analytics, no campaign tracking, and no way to monitor multiple competitors efficiently. For serious competitive intelligence, you need more.' },
      { heading: 'How RADAR is different', body: 'RADAR builds on the transparency of ad libraries with powerful intelligence features. Automated monitoring, real-time alerts, creative archives, and competitive analytics that turn raw ad data into strategic advantage.' },
      { heading: 'Made for Shopify merchants', body: 'Unlike generic ad spy tools, RADAR is purpose-built for e-commerce. Every feature is designed around the workflows and decisions that Shopify merchants face every day.' },
    ],
    features: ['Automated monitoring (no manual searching)', 'Real-time alerts for new competitor ads', 'Creative archive with search and filtering', 'Competitive analytics and trend detection', 'Multi-competitor tracking in one dashboard', 'Shopify-native integration'],
    cta: 'Try RADAR free — better than Meta Ads Library',
    relatedLinks: [{ label: 'Meta Ads Spy Tool', href: '/meta-ads-spy-tool' }, { label: 'Free Meta Ads Search', href: '/tools/meta-ads-search' }],
  },
  '/ad-script-generator': {
    title: 'Ad Script Generator',
    h1: 'Generate high-converting ad scripts in seconds',
    subtitle: 'Use competitive intelligence from RADAR to create ad scripts that outperform your competition.',
    sections: [
      { heading: 'Why competitor-informed scripts win', body: 'The best ad scripts are not created in a vacuum. They are informed by what is working in your market. RADAR shows you which competitor ads are running longest — a strong signal of performance — so you can model your scripts on proven patterns.' },
      { heading: 'How it works', body: 'RADAR analyzes competitor ad copy patterns across Meta, TikTok, and Google. Use these insights to craft scripts that speak to the same pain points and desires your audience responds to.' },
      { heading: 'From intelligence to action', body: 'RADAR is not just a spy tool — it is a competitive advantage platform. The ad script generator bridges the gap between intelligence and execution.' },
    ],
    features: ['Competitor ad copy analysis', 'High-performing pattern detection', 'Framework templates for video and text ads', 'Channel-specific script formats', 'Hook and CTA optimization suggestions', 'Export and share with team'],
    cta: 'Try RADAR free and generate better scripts',
    relatedLinks: [{ label: 'Meta Ads Intelligence', href: '/solutions/meta-ads' }, { label: 'TikTok Ads Intelligence', href: '/solutions/tiktok-ads' }],
  },
  '/google-ads-competitor-analysis': {
    title: 'Google Ads Competitor Analysis',
    h1: 'Deep Google Ads competitor analysis for e-commerce',
    subtitle: 'Understand exactly how your competitors use Google Ads. Track keywords, ad copy, shopping campaigns, and spend patterns.',
    sections: [
      { heading: 'Why Google Ads analysis matters', body: 'Google Ads drives significant revenue for e-commerce brands. Knowing which keywords competitors bid on, what ad copy they use, and how they structure campaigns gives you a strategic edge.' },
      { heading: 'What RADAR reveals', body: 'Track competitor search ads, display campaigns, and Google Shopping listings. See keyword overlaps, ad copy strategies, extension usage, and estimated budget allocation.' },
      { heading: 'Turn analysis into advantage', body: 'RADAR does not just show you competitor ads — it helps you understand strategy. Identify gaps in competitor coverage, find keyword opportunities, and optimize your own campaigns based on competitive intelligence.' },
    ],
    features: ['Search ad keyword tracking', 'Ad copy and extension analysis', 'Google Shopping monitoring', 'Keyword overlap identification', 'Budget estimation', 'Landing page analysis'],
    cta: 'Try RADAR free for Google Ads intelligence',
    relatedLinks: [{ label: 'Google Ads Solution', href: '/solutions/google-ads' }, { label: 'Free Google Ads Search', href: '/tools/google-ads-search' }],
  },
};

export default function SEOPageNew() {
  const location = useLocation();
  const data = seoPages[location.pathname];

  if (!data) {
    return (
      <Section hero>
        <Container>
          <h1 className="text-2xl font-display font-bold text-foreground">Page not found</h1>
          <Link to="/" className="text-lime-400 hover:text-lime-300 mt-4 inline-block">Go home</Link>
        </Container>
      </Section>
    );
  }

  return (
    <>
      <SEO
        title={data.title}
        description={data.subtitle}
        canonical={location.pathname}
        structuredData={breadcrumbSchema([
          { name: 'Home', href: '/' },
          { name: data.title, href: location.pathname },
        ])}
      />
      {/* Hero */}
      <Section hero className="relative overflow-hidden">
        <div className="absolute inset-0 radar-glow-hero opacity-40" />
        <Container className="relative z-10">
          <AnimatedReveal>
            <SignalChip variant="lime" className="mb-4">{data.title}</SignalChip>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold tracking-tight text-foreground mb-4 max-w-[700px]">
              {data.h1}
            </h1>
            <p className="text-base md:text-lg text-muted-foreground max-w-[560px] mb-8">{data.subtitle}</p>
            <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]">
              {data.cta} <ArrowRight className="h-4 w-4" />
            </Link>
          </AnimatedReveal>
        </Container>
      </Section>

      {/* Content sections */}
      <Section>
        <Container>
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Main content */}
            <div className="lg:col-span-2 space-y-12">
              {data.sections.map((s, i) => (
                <AnimatedReveal key={i} delay={i * 0.1}>
                  <h2 className="text-xl font-display font-bold text-foreground mb-4">{s.heading}</h2>
                  <p className="text-sm text-muted-foreground leading-relaxed">{s.body}</p>
                </AnimatedReveal>
              ))}

              {/* Features */}
              <AnimatedReveal>
                <h2 className="text-xl font-display font-bold text-foreground mb-6">Key features</h2>
                <ul className="space-y-3">
                  {data.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-foreground/80">
                      <Check className="h-4 w-4 text-lime-500 flex-shrink-0 mt-0.5" />
                      {f}
                    </li>
                  ))}
                </ul>
              </AnimatedReveal>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <AnimatedReveal delay={0.2}>
                <GlassPanel className="p-6 sticky top-24">
                  <h3 className="text-base font-display font-semibold text-foreground mb-4">Start monitoring today</h3>
                  <p className="text-sm text-muted-foreground mb-6">7-day free trial. No credit card required.</p>
                  <Link to="/register" className="block text-center py-3 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors mb-6">
                    Start free trial
                  </Link>
                  {data.relatedLinks && (
                    <div>
                      <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-3">Related</p>
                      <div className="space-y-2">
                        {data.relatedLinks.map((rl, i) => (
                          <Link key={i} to={rl.href} className="flex items-center gap-2 text-sm text-foreground/70 hover:text-lime-400 transition-colors">
                            <ChevronRight className="h-3.5 w-3.5" /> {rl.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </GlassPanel>
              </AnimatedReveal>
            </div>
          </div>
        </Container>
      </Section>

      {/* CTA */}
      <Section className="border-t border-white/[0.04]">
        <Container>
          <AnimatedReveal>
            <GlassPanel className="p-10 sm:p-14 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-b from-lime-500/[0.03] to-transparent pointer-events-none" />
              <div className="relative z-10">
                <h2 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">Ready to get started?</h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">Join thousands of Shopify merchants using RADAR for competitive intelligence.</p>
                <Link to="/register" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-xl bg-[#CCFF6C] text-black text-sm font-semibold hover:bg-[#D9FF9A] transition-colors shadow-[0_0_30px_rgba(204,255,108,0.2)]">
                  {data.cta} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </GlassPanel>
          </AnimatedReveal>
        </Container>
      </Section>
    </>
  );
}
